import { useQuery } from "@tanstack/react-query"
import _ from "lodash"
import {
  GroupOverviewStudies,
  GroupOverviewStudy,
  IPatient,
  IScore,
  PatientOverview
} from "../types/types"
import { apiFetchProtected } from "../utils/api"

const fecthGroupOverviewStudies = async (): Promise<GroupOverviewStudies> =>
  apiFetchProtected<GroupOverviewStudies>("/group_overview/studies/", {}).then(
    (data) => {
      return data.map((study) => {
        return {
          ...study,
          patients: study.patients.sort((a, b) => {
            const a_date = new Date(a.surgery_date)
            const b_date = new Date(b.surgery_date)
            return a_date.valueOf() - b_date.valueOf()
          }),
          scores: study.scores.sort((a, b) => {
            const a_date = new Date(a.created_at)
            const b_date = new Date(b.created_at)
            return b_date.valueOf() - a_date.valueOf()
          })
        }
      })
    }
  )

export const useGroupOverviewStudiesQuery = <TData = GroupOverviewStudies>(
  select?: (data: GroupOverviewStudies) => TData
) =>
  useQuery({
    queryKey: ["group_overview", "studies"],
    queryFn: fecthGroupOverviewStudies,
    select
  })

export const useScoresByMonth = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      const study = data.find((_study) => _study.study.id === study_id)
      return study ? calculateScores(study) : undefined
    }
    const groupScores = data.map(calculateScores)
    return {
      "@3days": groupScores.map((_score) => _score["@3days"]).flat(),
      "@15days": groupScores.map((_score) => _score["@15days"]).flat(),
      "@30days": groupScores.map((_score) => _score["@30days"]).flat(),
      "@45days": groupScores.map((_score) => _score["@45days"]).flat(),
      "@2months": groupScores.map((_score) => _score["@2months"]).flat(),
      "@3months": groupScores.map((_score) => _score["@3months"]).flat(),
      "@6months": groupScores.map((_score) => _score["@6months"]).flat()
    }
  })

export const useScoresCount = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      const study = data.find((_study) => _study.study.id === study_id)
      return study ? study.scores.length : 0
    }
    return data.map((study) => study.scores).flat().length
  })

export const usePatientsCount = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      const study = data.find((_study) => _study.study.id === study_id)
      return study ? study.patients.length : 0
    }
    return data.map((study) => study.patients).flat().length
  })

export const useSubmssionsCount = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      const study = data.find((_study) => _study.study.id === study_id)
      return study ? study.submissions : 0
    }
    return data.map((study) => study.submissions).reduce((a, b) => a + b, 0)
  })

export const usePatientsAge = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      const study = data.find((_study) => _study.study.id === study_id)
      return study
        ? study.patients.map(({ birthday }) => calculateAge(birthday))
        : undefined
    }
    return data
      .map((study) =>
        study.patients.map(({ birthday }) => calculateAge(birthday))
      )
      .flat()
  })

export const usePatientsSurgeryDates = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      return _.groupBy(data[study_id].patients, ({ surgery_date }) =>
        surgery_date.split("-").slice(0, -1).join("-")
      )
    }
  })
export const useBaselineScoresByPatients = (study_id?: number) =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    if (study_id) {
      const study = data.find((_study) => _study.study.id === study_id)
      return study ? calculateBaselineScore(study) : undefined
    }
    const groupBaselineScores = data.map(calculateBaselineScore)
    return groupBaselineScores.flat()
  })

export const useCounts = () => {
  useGroupOverviewStudiesQuery(aggregateCounts)
}

export const useStudyValues = () =>
  useGroupOverviewStudiesQuery((data: GroupOverviewStudies) => {
    return data.map((study) => study.study)
  })

const aggregateCounts = (data: GroupOverviewStudies) => {
  const patientsCount = data.reduce(
    (accumulator, currentValue) => accumulator + currentValue.patients.length,
    0
  )
  const scoresCount = data.reduce(
    (accumulator, currentValue) => accumulator + currentValue.scores.length,
    0
  )

  return {
    patientsCount,
    scoresCount
  }
}

const calculateScores = (study: GroupOverviewStudy) => {
  const scoresById = _.groupBy(study.scores, "patient_id")
  const patientsByID = _.groupBy(study.patients, "id")
  const threeDaysScore = calculateScore(scoresById, patientsByID, {
    mode: "days",
    value: 3
  })
  const fifteenDaysScore = calculateScore(scoresById, patientsByID, {
    mode: "days",
    value: 15
  })
  const oneMonthScore = calculateScore(scoresById, patientsByID, {
    mode: "months",
    value: 1
  })
  const fortyFiveDaysScore = calculateScore(scoresById, patientsByID, {
    mode: "days",
    value: 45
  })
  const twoMonthsScore = calculateScore(scoresById, patientsByID, {
    mode: "months",
    value: 2
  })
  const threeMonthsScore = calculateScore(scoresById, patientsByID, {
    mode: "months",
    value: 3
  })
  const sixMonthsScore = calculateScore(scoresById, patientsByID, {
    mode: "months",
    value: 6
  })
  return {
    "@3days": threeDaysScore.filter((_score) => _score !== undefined),
    "@15days": fifteenDaysScore.filter((_score) => _score !== undefined),
    "@30days": oneMonthScore.filter((_score) => _score !== undefined),
    "@45days": fortyFiveDaysScore.filter((_score) => _score !== undefined),
    "@2months": twoMonthsScore.filter((_score) => _score !== undefined),
    "@3months": threeMonthsScore.filter((_score) => _score !== undefined),
    "@6months": sixMonthsScore.filter((_score) => _score !== undefined)
  }
}

type Period = {
  mode: "days" | "months"
  value: number
}

const calculateScore = (
  scoresById: _.Dictionary<IScore[]>,
  patientsByID: _.Dictionary<PatientOverview[]>,
  period: Period
) =>
  Object.entries(scoresById).map(([key, val]) => {
    const baselineDate = new Date(patientsByID[key][0].surgery_date)
    if (period.mode === "days")
      baselineDate.setDate(baselineDate.getDay() + period.value)
    if (period.mode === "months")
      baselineDate.setMonth(baselineDate.getMonth() + period.value)

    const scoreIndex = val.findIndex((_score, idx) => {
      const scoreDate = new Date(_score["created_at"])
      const diff = scoreDate.getTime() - baselineDate.getTime()
      if (diff < 0) return true
      return false
    })
    return val[scoreIndex - 1]
  })

const calculateBaselineScore = (study: GroupOverviewStudy) => {
  const scoresById = _.groupBy(study.scores, "patient_id")
  return Object.entries(scoresById).map(([key, val]) => {
    return val[val.length - 1]
  })
}

const calculateAge = (birthday: string) => {
  const dateOfBirth = new Date(birthday)
  const today = new Date()
  const diffInMilliSeconds = today.getTime() - dateOfBirth.getTime()
  const diffInYears = diffInMilliSeconds / 1000 / 60 / 60 / 24 / 365.25
  return Math.abs(Math.round(diffInYears))
}
